// Fixture data for the new SOR marketplace remake.
// All IDs are stable so the router can deep-link.

export type Category = {
  id: string;
  name: string;
  tagline: string;
  image: string;
  serviceCount: number;
  subcategories: { id: string; name: string; count: number }[];
};

export type Vendor = {
  id: string;
  name: string;
  headline: string;
  location: string;
  yearFounded: number;
  avatar: string;
  cover: string;
  rating: number;
  reviewCount: number;
  verified: boolean;
  responseTime: string;
  completedProjects: number;
  categories: string[]; // category ids
  bio: string;
  portfolio: { id: string; image: string; title: string }[];
  languages: string[];
};

export type Package = {
  id: string;
  tier: 'essential' | 'considered' | 'signature';
  name: string;
  priceFrom: number;
  currency: 'AZN';
  duration: string;
  includes: string[];
};

export type Service = {
  id: string;
  title: string;
  vendorId: string;
  categoryId: string;
  subcategoryId: string;
  heroImage: string;
  gallery: string[];
  summary: string;
  description: string;
  rating: number;
  reviewCount: number;
  priceFrom: number;
  currency: 'AZN';
  packages: Package[];
  tags: string[];
};

export type Review = {
  id: string;
  author: string;
  avatar: string;
  rating: number;
  date: string;
  body: string;
  serviceId: string;
};

// ─── Categories ────────────────────────────────────────────────────────────

export const categories: Category[] = [
  {
    id: 'renovation',
    name: 'Renovation',
    tagline: 'Full and partial home renovations',
    image:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 184,
    subcategories: [
      { id: 'full-reno', name: 'Full apartment renovation', count: 62 },
      { id: 'kitchen', name: 'Kitchen renovation', count: 48 },
      { id: 'bathroom', name: 'Bathroom renovation', count: 41 },
      { id: 'partial', name: 'Partial renovation', count: 33 },
    ],
  },
  {
    id: 'interior-design',
    name: 'Interior design',
    tagline: 'Studios who plan the room before the trade arrives',
    image:
      'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 92,
    subcategories: [
      { id: 'residential', name: 'Residential design', count: 54 },
      { id: 'commercial', name: 'Commercial design', count: 22 },
      { id: '3d-visual', name: '3D visualisation', count: 16 },
    ],
  },
  {
    id: 'electrical',
    name: 'Electrical',
    tagline: 'Wiring, panels, and smart home installs',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 137,
    subcategories: [
      { id: 'rewire', name: 'Full rewiring', count: 38 },
      { id: 'panel', name: 'Panel upgrade', count: 29 },
      { id: 'smart-home', name: 'Smart home', count: 31 },
      { id: 'troubleshoot', name: 'Troubleshooting', count: 39 },
    ],
  },
  {
    id: 'plumbing',
    name: 'Plumbing',
    tagline: 'From leaks to full bathroom re-piping',
    image:
      'https://images.unsplash.com/photo-1581244277943-fe4a9c777189?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 128,
    subcategories: [
      { id: 'repair', name: 'Repair & leaks', count: 52 },
      { id: 'installation', name: 'New installation', count: 44 },
      { id: 'boilers', name: 'Boilers & heaters', count: 32 },
    ],
  },
  {
    id: 'painting',
    name: 'Painting & finishes',
    tagline: 'Interior, exterior and decorative finishes',
    image:
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 96,
    subcategories: [
      { id: 'interior-paint', name: 'Interior paint', count: 48 },
      { id: 'exterior-paint', name: 'Exterior paint', count: 27 },
      { id: 'decorative', name: 'Decorative', count: 21 },
    ],
  },
  {
    id: 'windows-doors',
    name: 'Windows & doors',
    tagline: 'Measurement, supply and install',
    image:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 74,
    subcategories: [
      { id: 'windows', name: 'Windows', count: 41 },
      { id: 'doors', name: 'Doors', count: 33 },
    ],
  },
  {
    id: 'flooring',
    name: 'Flooring',
    tagline: 'Hardwood, tile, laminate and polished concrete',
    image:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 89,
    subcategories: [
      { id: 'hardwood', name: 'Hardwood', count: 31 },
      { id: 'tile', name: 'Tile', count: 36 },
      { id: 'laminate', name: 'Laminate & vinyl', count: 22 },
    ],
  },
  {
    id: 'hvac',
    name: 'Heating & cooling',
    tagline: 'Split systems, central air, floor heating',
    image:
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 58,
    subcategories: [
      { id: 'ac', name: 'Air conditioning', count: 28 },
      { id: 'heating', name: 'Heating', count: 19 },
      { id: 'ventilation', name: 'Ventilation', count: 11 },
    ],
  },
  {
    id: 'furniture',
    name: 'Custom furniture',
    tagline: 'Millwork, built-ins, and solid-wood pieces',
    image:
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 67,
    subcategories: [
      { id: 'kitchens', name: 'Kitchen cabinetry', count: 24 },
      { id: 'built-ins', name: 'Built-ins & wardrobes', count: 28 },
      { id: 'tables', name: 'Tables & seating', count: 15 },
    ],
  },
  {
    id: 'roofing',
    name: 'Roofing',
    tagline: 'Repair, replacement and waterproofing',
    image:
      'https://images.unsplash.com/photo-1605152276897-4f618f831968?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 42,
    subcategories: [
      { id: 'repair-roof', name: 'Repair', count: 22 },
      { id: 'replace-roof', name: 'Replacement', count: 14 },
      { id: 'waterproof', name: 'Waterproofing', count: 6 },
    ],
  },
  {
    id: 'landscaping',
    name: 'Landscaping',
    tagline: 'Outdoor living, gardens and terraces',
    image:
      'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 38,
    subcategories: [
      { id: 'garden', name: 'Garden design', count: 18 },
      { id: 'terrace', name: 'Terrace & deck', count: 14 },
      { id: 'irrigation', name: 'Irrigation', count: 6 },
    ],
  },
  {
    id: 'moving',
    name: 'Moving & logistics',
    tagline: 'Residential moves and careful furniture handling',
    image:
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 29,
    subcategories: [
      { id: 'residential-move', name: 'Residential moves', count: 17 },
      { id: 'furniture-move', name: 'Furniture handling', count: 12 },
    ],
  },
  {
    id: 'cleaning',
    name: 'Deep cleaning',
    tagline: 'Post-renovation and recurring deep cleans',
    image:
      'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80',
    serviceCount: 52,
    subcategories: [
      { id: 'post-reno', name: 'Post-renovation', count: 21 },
      { id: 'deep', name: 'Deep cleaning', count: 19 },
      { id: 'move-clean', name: 'Move-in / move-out', count: 12 },
    ],
  },
];

// ─── Vendors ───────────────────────────────────────────────────────────────

export const vendors: Vendor[] = [
  {
    id: 'atelier-nord',
    name: 'Atelier Nord',
    headline: 'Calm, considered residential renovations',
    location: 'Baku, Nasimi',
    yearFounded: 2016,
    avatar:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
    rating: 4.9,
    reviewCount: 87,
    verified: true,
    responseTime: 'within 2 hours',
    completedProjects: 142,
    categories: ['renovation', 'interior-design'],
    bio: 'Atelier Nord is a Baku-based renovation studio known for restrained palettes, honest materials, and quietly ambitious carpentry. We take on six to eight full renovations each year.',
    portfolio: [
      {
        id: 'p1',
        image:
          'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=80',
        title: 'Icherisheher duplex',
      },
      {
        id: 'p2',
        image:
          'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1000&q=80',
        title: 'Yasamal family home',
      },
      {
        id: 'p3',
        image:
          'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
        title: 'Port Baku pied-à-terre',
      },
      {
        id: 'p4',
        image:
          'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
        title: 'Nizami boulevard flat',
      },
      {
        id: 'p5',
        image:
          'https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1000&q=80',
        title: 'White Villa, Mardakan',
      },
      {
        id: 'p6',
        image:
          'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1000&q=80',
        title: 'Olive House studio',
      },
    ],
    languages: ['Azerbaijani', 'Russian', 'English'],
  },
  {
    id: 'meydan-works',
    name: 'Meydan Works',
    headline: 'Electrical that disappears into the walls',
    location: 'Baku, Yasamal',
    yearFounded: 2012,
    avatar:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=80',
    rating: 4.8,
    reviewCount: 124,
    verified: true,
    responseTime: 'within 1 hour',
    completedProjects: 318,
    categories: ['electrical'],
    bio: 'Second-generation electricians specialising in full rewires, panel upgrades, and smart-home installs across Baku apartments and villas.',
    portfolio: [
      {
        id: 'm1',
        image:
          'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1000&q=80',
        title: 'Heydar Aliyev Avenue rewire',
      },
      {
        id: 'm2',
        image:
          'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1000&q=80',
        title: 'Smart lighting, Port Baku',
      },
      {
        id: 'm3',
        image:
          'https://images.unsplash.com/photo-1504306057900-adad59c2c1b8?auto=format&fit=crop&w=1000&q=80',
        title: 'Commercial panel upgrade',
      },
      {
        id: 'm4',
        image:
          'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1000&q=80',
        title: 'Villa generator install',
      },
    ],
    languages: ['Azerbaijani', 'Russian'],
  },
  {
    id: 'sayad-stoneworks',
    name: 'Sayad Stoneworks',
    headline: 'Hand-cut stone, tile, and terrazzo',
    location: 'Baku, Absheron',
    yearFounded: 2009,
    avatar:
      'https://images.unsplash.com/photo-1463453091185-61582044d556?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=1600&q=80',
    rating: 4.9,
    reviewCount: 63,
    verified: true,
    responseTime: 'within 4 hours',
    completedProjects: 96,
    categories: ['flooring', 'renovation'],
    bio: 'A family stoneworks laying terrazzo, hand-cut tile, and limestone floors for restaurants, homes and cultural projects.',
    portfolio: [
      {
        id: 's1',
        image:
          'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
        title: 'Fountain Square restaurant',
      },
      {
        id: 's2',
        image:
          'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?auto=format&fit=crop&w=1000&q=80',
        title: 'Olive House lobby',
      },
      {
        id: 's3',
        image:
          'https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1000&q=80',
        title: 'Absheron villa',
      },
    ],
    languages: ['Azerbaijani', 'Turkish'],
  },
  {
    id: 'karvan-joinery',
    name: 'Karvan Joinery',
    headline: 'Built-ins, wardrobes and solid-wood kitchens',
    location: 'Baku, Sabail',
    yearFounded: 2014,
    avatar:
      'https://images.unsplash.com/photo-1545996124-0501ebae84d0?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1600&q=80',
    rating: 4.7,
    reviewCount: 48,
    verified: true,
    responseTime: 'within 6 hours',
    completedProjects: 74,
    categories: ['furniture', 'renovation'],
    bio: 'We design and build kitchens, library walls, and wardrobes in oak, walnut and ash. Everything is drawn, cut, and finished in our Baku workshop.',
    portfolio: [
      {
        id: 'k1',
        image:
          'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1000&q=80',
        title: 'Oak library wall',
      },
      {
        id: 'k2',
        image:
          'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80',
        title: 'Walnut kitchen',
      },
      {
        id: 'k3',
        image:
          'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1000&q=80',
        title: 'Hallway cabinetry',
      },
    ],
    languages: ['Azerbaijani', 'Russian', 'English'],
  },
  {
    id: 'studio-lumen',
    name: 'Studio Lumen',
    headline: 'Interior design with light as the first material',
    location: 'Baku, Nasimi',
    yearFounded: 2018,
    avatar:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1600&q=80',
    rating: 5.0,
    reviewCount: 29,
    verified: true,
    responseTime: 'within 24 hours',
    completedProjects: 31,
    categories: ['interior-design'],
    bio: 'A small design studio led by Leyla Rzayeva. We work on residential interiors that begin with a lighting study rather than a mood board.',
    portfolio: [
      {
        id: 'l1',
        image:
          'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1000&q=80',
        title: 'Olive dusk apartment',
      },
      {
        id: 'l2',
        image:
          'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
        title: 'Sabail townhouse',
      },
      {
        id: 'l3',
        image:
          'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1000&q=80',
        title: 'Courtyard villa',
      },
    ],
    languages: ['Azerbaijani', 'English'],
  },
  {
    id: 'marina-paint-co',
    name: 'Marina Paint Co.',
    headline: 'Mineral paints and decorative finishes',
    location: 'Baku, Narimanov',
    yearFounded: 2015,
    avatar:
      'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=400&q=80',
    cover:
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1600&q=80',
    rating: 4.8,
    reviewCount: 71,
    verified: true,
    responseTime: 'within 2 hours',
    completedProjects: 156,
    categories: ['painting'],
    bio: 'A painting team specialising in low-VOC mineral paints, lime wash, and decorative finishes for residential interiors.',
    portfolio: [
      {
        id: 'mp1',
        image:
          'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1000&q=80',
        title: 'Lime wash bedroom',
      },
      {
        id: 'mp2',
        image:
          'https://images.unsplash.com/photo-1600566753052-dc5d4b9a6b2a?auto=format&fit=crop&w=1000&q=80',
        title: 'Heritage restoration',
      },
    ],
    languages: ['Azerbaijani', 'Russian'],
  },
];

// ─── Services ──────────────────────────────────────────────────────────────

export const services: Service[] = [
  {
    id: 'atelier-nord-full-reno',
    title: 'Full apartment renovation, turn-key',
    vendorId: 'atelier-nord',
    categoryId: 'renovation',
    subcategoryId: 'full-reno',
    heroImage:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1000&q=80',
    ],
    summary:
      'A considered turn-key renovation for 60–140 m² Baku apartments, delivered in 10–14 weeks.',
    description:
      'We plan, procure and build — drawings, demolition, trades, finishes and snagging. Fixed-price with a stone-weighted contingency, weekly updates on camera, and a single point of contact across the entire build.',
    rating: 4.9,
    reviewCount: 42,
    priceFrom: 14500,
    currency: 'AZN',
    packages: [
      {
        id: 'essential',
        tier: 'essential',
        name: 'Essential refresh',
        priceFrom: 14500,
        currency: 'AZN',
        duration: '6–8 weeks',
        includes: [
          'Full plan and drawings',
          'Demolition and trades',
          'Standard finishes',
          'Weekly progress photos',
        ],
      },
      {
        id: 'considered',
        tier: 'considered',
        name: 'Considered renovation',
        priceFrom: 24500,
        currency: 'AZN',
        duration: '10–12 weeks',
        includes: [
          'Everything in Essential',
          'Lighting study',
          'Mid-tier finishes and fixtures',
          'Dedicated site manager',
        ],
      },
      {
        id: 'signature',
        tier: 'signature',
        name: 'Signature build',
        priceFrom: 42000,
        currency: 'AZN',
        duration: '14–18 weeks',
        includes: [
          'Everything in Considered',
          'Custom joinery by Karvan',
          'Imported stone and tile',
          '12-month post-handover support',
        ],
      },
    ],
    tags: ['turn-key', 'fixed-price', 'weekly-updates'],
  },
  {
    id: 'meydan-full-rewire',
    title: 'Full apartment rewire with panel upgrade',
    vendorId: 'meydan-works',
    categoryId: 'electrical',
    subcategoryId: 'rewire',
    heroImage:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1504306057900-adad59c2c1b8?auto=format&fit=crop&w=1000&q=80',
    ],
    summary:
      'Full rewire with a modern panel, tested and certified, done cleanly around existing furniture.',
    description:
      'We pull every old cable, chase fresh runs, install a new consumer unit, then patch and paint behind us. Testing and certification included. Typical for 1- to 3-bedroom apartments.',
    rating: 4.8,
    reviewCount: 38,
    priceFrom: 2800,
    currency: 'AZN',
    packages: [
      {
        id: 'rewire-essential',
        tier: 'essential',
        name: '1-bed rewire',
        priceFrom: 2800,
        currency: 'AZN',
        duration: '4–5 days',
        includes: ['Up to 60 m²', 'New panel', 'Testing + certificate'],
      },
      {
        id: 'rewire-considered',
        tier: 'considered',
        name: '2–3 bed rewire',
        priceFrom: 4200,
        currency: 'AZN',
        duration: '6–8 days',
        includes: ['Up to 120 m²', 'New panel', 'Smart switches ready'],
      },
      {
        id: 'rewire-signature',
        tier: 'signature',
        name: 'Villa + smart home',
        priceFrom: 8500,
        currency: 'AZN',
        duration: '2–3 weeks',
        includes: [
          'Up to 300 m²',
          'Smart home backbone',
          'Generator tie-in',
          'Commissioning',
        ],
      },
    ],
    tags: ['certified', 'clean-work', 'smart-home'],
  },
  {
    id: 'studio-lumen-residential',
    title: 'Residential interior design, from brief to build',
    vendorId: 'studio-lumen',
    categoryId: 'interior-design',
    subcategoryId: 'residential',
    heroImage:
      'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
    ],
    summary:
      'Full interior design including lighting study, FF&E selection, and build supervision.',
    description:
      'A design engagement that begins with a lighting study and ends on site. We draw, specify, source, and supervise — handing the build to your chosen contractor.',
    rating: 5.0,
    reviewCount: 21,
    priceFrom: 3200,
    currency: 'AZN',
    packages: [
      {
        id: 'lumen-essential',
        tier: 'essential',
        name: 'Concept package',
        priceFrom: 3200,
        currency: 'AZN',
        duration: '3 weeks',
        includes: ['Mood & palette', 'Floor plan', 'Lighting plan'],
      },
      {
        id: 'lumen-considered',
        tier: 'considered',
        name: 'Full design',
        priceFrom: 7800,
        currency: 'AZN',
        duration: '6–8 weeks',
        includes: [
          'Concept package',
          'FF&E list',
          '3D visuals',
          'Build drawings',
        ],
      },
      {
        id: 'lumen-signature',
        tier: 'signature',
        name: 'Full design + supervision',
        priceFrom: 14500,
        currency: 'AZN',
        duration: 'through build',
        includes: [
          'Full design package',
          'Weekly site visits',
          'Snagging',
          'Styling for handover',
        ],
      },
    ],
    tags: ['lighting-first', 'full-service'],
  },
  {
    id: 'sayad-terrazzo',
    title: 'Hand-poured terrazzo flooring',
    vendorId: 'sayad-stoneworks',
    categoryId: 'flooring',
    subcategoryId: 'tile',
    heroImage:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1560185127-6ed189bf02f4?auto=format&fit=crop&w=1000&q=80',
    ],
    summary:
      'Custom-poured terrazzo floors with your chosen aggregate mix, on-site.',
    description:
      'We sample, pour, grind, seal. Every floor is unique — we hold a sample session with you before the pour to agree the mix and grade.',
    rating: 4.9,
    reviewCount: 24,
    priceFrom: 185,
    currency: 'AZN',
    packages: [
      {
        id: 'sayad-essential',
        tier: 'essential',
        name: 'Standard pour',
        priceFrom: 185,
        currency: 'AZN',
        duration: 'per m²',
        includes: ['Standard aggregate', 'Two-stage grind', 'Seal coat'],
      },
      {
        id: 'sayad-considered',
        tier: 'considered',
        name: 'Custom pour',
        priceFrom: 240,
        currency: 'AZN',
        duration: 'per m²',
        includes: ['Custom mix', 'Three-stage grind', 'Honed finish'],
      },
    ],
    tags: ['on-site-pour', 'custom-mix'],
  },
  {
    id: 'karvan-kitchen',
    title: 'Solid-wood kitchen, drawn and built to order',
    vendorId: 'karvan-joinery',
    categoryId: 'furniture',
    subcategoryId: 'kitchens',
    heroImage:
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1000&q=80',
    ],
    summary: 'Oak, walnut or ash kitchens with hand-fitted hardware.',
    description:
      'We draw, cut, finish and install — including stone countertop supply and appliance integration. Lead time 8–12 weeks from sign-off.',
    rating: 4.7,
    reviewCount: 26,
    priceFrom: 9800,
    currency: 'AZN',
    packages: [
      {
        id: 'karvan-essential',
        tier: 'essential',
        name: 'Compact kitchen',
        priceFrom: 9800,
        currency: 'AZN',
        duration: '8 weeks',
        includes: ['Up to 6 linear meters', 'Oak fronts', 'Standard hardware'],
      },
      {
        id: 'karvan-considered',
        tier: 'considered',
        name: 'Full kitchen',
        priceFrom: 16500,
        currency: 'AZN',
        duration: '10 weeks',
        includes: [
          'Up to 10 linear meters',
          'Walnut or ash fronts',
          'Hand-fitted hardware',
          'Stone worktop supply',
        ],
      },
    ],
    tags: ['solid-wood', 'made-to-order'],
  },
  {
    id: 'marina-limewash',
    title: 'Lime wash and mineral-paint interior',
    vendorId: 'marina-paint-co',
    categoryId: 'painting',
    subcategoryId: 'decorative',
    heroImage:
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1600&q=80',
    gallery: [
      'https://images.unsplash.com/photo-1600566753052-dc5d4b9a6b2a?auto=format&fit=crop&w=1000&q=80',
    ],
    summary: 'Lime wash, Venetian plaster or mineral paint for whole rooms.',
    description:
      'Low-VOC, breathable finishes applied by a small team that treats paint as a craft. Sample panels are made on site before you commit.',
    rating: 4.8,
    reviewCount: 34,
    priceFrom: 42,
    currency: 'AZN',
    packages: [
      {
        id: 'marina-essential',
        tier: 'essential',
        name: 'Mineral paint',
        priceFrom: 42,
        currency: 'AZN',
        duration: 'per m²',
        includes: ['Low-VOC paint', 'Two coats', 'Minor prep'],
      },
      {
        id: 'marina-considered',
        tier: 'considered',
        name: 'Lime wash',
        priceFrom: 68,
        currency: 'AZN',
        duration: 'per m²',
        includes: ['Lime wash', 'Sample panel', 'Full prep'],
      },
    ],
    tags: ['low-voc', 'decorative'],
  },
];

// ─── Reviews ───────────────────────────────────────────────────────────────

export const reviews: Review[] = [
  {
    id: 'r1',
    author: 'Aygun M.',
    avatar:
      'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=200&q=80',
    rating: 5,
    date: 'March 2026',
    body: 'Atelier Nord delivered exactly what the drawings promised. Weekly updates were calm and clear. The kitchen is the quietest room of the apartment now.',
    serviceId: 'atelier-nord-full-reno',
  },
  {
    id: 'r2',
    author: 'Rauf S.',
    avatar:
      'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=200&q=80',
    rating: 5,
    date: 'February 2026',
    body: 'Meydan rewired our 3-bed flat around our kids staying home. No dust left behind. They re-did one outlet for free after a small fault — no fuss.',
    serviceId: 'meydan-full-rewire',
  },
  {
    id: 'r3',
    author: 'Leyla H.',
    avatar:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80',
    rating: 5,
    date: 'January 2026',
    body: 'Studio Lumen started with a lighting study before any finishes. The apartment feels completely different at dusk — and the contractor had zero ambiguity about what to build.',
    serviceId: 'studio-lumen-residential',
  },
  {
    id: 'r4',
    author: 'Emin A.',
    avatar:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&q=80',
    rating: 5,
    date: 'December 2025',
    body: 'The terrazzo in our restaurant turned out better than the samples. Sayad treats it as a craft, not a trade.',
    serviceId: 'sayad-terrazzo',
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────

export function getCategoryById(id: string) {
  return categories.find((c) => c.id === id);
}

export function getVendorById(id: string) {
  return vendors.find((v) => v.id === id);
}

export function getServiceById(id: string) {
  return services.find((s) => s.id === id);
}

export function getServicesByCategory(categoryId: string) {
  return services.filter((s) => s.categoryId === categoryId);
}

export function getServicesByVendor(vendorId: string) {
  return services.filter((s) => s.vendorId === vendorId);
}

export function getReviewsForService(serviceId: string) {
  return reviews.filter((r) => r.serviceId === serviceId);
}

export function getFeaturedVendors(n = 4) {
  return vendors.slice(0, n);
}

export function getPopularServices(n = 6) {
  return [...services]
    .sort((a, b) => b.reviewCount - a.reviewCount)
    .slice(0, n);
}
